// Nouvelle intervention : assistant « une question à la fois » (étapes numérotées : c'est une vraie séquence).
// 1) site puis équipement, 2) symptôme et codes défaut, 3) contexte. Retour arrière possible à chaque étape.
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Cpu, FlaskConical, PackagePlus, ScanText, Search } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { formulairePhoto, reduirePhoto } from '../../lib/photo.ts';
import {
  Alerte, Badge, Bouton, Champ, ChampTexte, Chargement, EntetePage, EtatErreur, EtatVide, LU_SUR_PHOTO, MarqueIa, Panneau, Valeur,
} from '../../components/ui.tsx';
import { BoutonPhoto, useLecturePhoto, type Afficheur } from '../../components/LecturePhoto.tsx';
import { cheminLieu, type Lieu } from '../sites/FormulaireEquipement.tsx';
import { DEPUIS, type ContexteIntervention } from './types.ts';

interface SiteResume { id: string; name: string; city: string | null; demo: boolean; client_name: string | null; nb_equipements: number }
interface EquipementChoix {
  id: string; label: string; category: string | null; manufacturer: string | null; range_name: string | null; reference: string | null;
  sw_version?: string | null; firmware?: string | null; location_id?: string | null; site_id?: string; site_name?: string; demo?: boolean;
}
interface SiteDetail { id: string; name: string; demo: boolean; lieux: Lieu[]; equipements: EquipementChoix[] }

/** Codes défaut saisis librement (« E05, F12 ») : séparés, normalisés, dédoublonnés. */
export function decouperCodes(s: string) {
  return [...new Set(s.split(/[\s,;]+/).map((c) => c.trim().toUpperCase()).filter(Boolean))].slice(0, 20);
}

function Puces<T extends string>({ libelle, options, valeur, changer }: { libelle: string; options: { v: T; l: string }[]; valeur: T | null | undefined; changer: (v: T | null) => void }) {
  return (
    <fieldset className="int-puces">
      <legend className="champ-libelle">{libelle}</legend>
      <div className="rangee" style={{ gap: 8 }}>
        {options.map((o) => (
          <button key={o.v} type="button" className={`btn int-puce ${valeur === o.v ? 'btn-principal' : ''}`} aria-pressed={valeur === o.v}
            onClick={() => changer(valeur === o.v ? null : o.v)}>{o.l}</button>
        ))}
      </div>
    </fieldset>
  );
}

const ETAPES = ['Équipement', 'Symptôme', 'Contexte'];

export function PageNouvelleIntervention() {
  const { peut } = useSession();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const paramEquipement = params.get('equipement');
  const [etape, setEtape] = useState(1);
  const [siteId, setSiteId] = useState<string | null>(params.get('site'));
  const [equipement, setEquipement] = useState<EquipementChoix | null>(null);
  const [sansEquipement, setSansEquipement] = useState(false);
  const [symptome, setSymptome] = useState('');
  const [codes, setCodes] = useState('');
  const [ctx, setCtx] = useState<ContexteIntervention>({});
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const lu = useLuSurPhoto();

  // Pré-remplissage depuis la fiche d'un équipement
  const pre = useQuery({
    queryKey: ['equipement', paramEquipement], enabled: !!paramEquipement,
    queryFn: () => api.get<EquipementChoix & { site: { id: string; name: string } }>(`/api/equipements/${paramEquipement}`),
  });
  useEffect(() => {
    if (pre.data) { setSiteId(pre.data.site.id); setEquipement(pre.data); setEtape(2); }
  }, [pre.data]);

  const site = useQuery({ queryKey: ['site', siteId], enabled: !!siteId, queryFn: () => api.get<SiteDetail>(`/api/sites/${siteId}`) });

  const aller = (n: number) => { setEtape(n); setErreur(null); window.scrollTo(0, 0); };

  const creer = async () => {
    if (!siteId) return;
    setEnvoi(true); setErreur(null);
    try {
      const contexte = { ...ctx, codes: decouperCodes(codes) };
      const r = await api.post<{ id: string }>('/api/interventions', {
        siteId, equipmentId: equipement?.id ?? null, symptom: symptome.trim() || null, context: contexte,
      });
      qc.invalidateQueries({ queryKey: ['interventions'] });
      nav(`/interventions/${r.id}`, { replace: true });
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };

  if (!peut('intervention.ecrire')) {
    return <EtatErreur erreur={{ code: 'droit_insuffisant', message: 'Vos droits ne permettent pas de créer une intervention.' }} />;
  }
  if (paramEquipement && pre.isLoading) return <Chargement lignes={4} />;
  if (paramEquipement && pre.isError) return <EtatErreur erreur={pre.error} />;

  const resume = site.data && (
    <p className="petit secondaire int-resume">
      <Building2 size={16} aria-hidden /> {site.data.name}
      {site.data.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
      {equipement && <><Cpu size={16} aria-hidden /> {equipement.label}{equipement.reference && <> — <span className="mono">{equipement.reference}</span></>}</>}
      {sansEquipement && <span className="discret">Équipement non désigné</span>}
    </p>
  );

  return (
    <div className="pile-l int-assistant">
      <EntetePage titre="Nouvelle intervention" sousTitre="Une question à la fois. Vous pourrez tout compléter ensuite." />
      <ol className="int-etapes" aria-label="Étapes">
        {ETAPES.map((e, k) => (
          <li key={e} className={k + 1 === etape ? 'est-active' : k + 1 < etape ? 'est-faite' : ''} aria-current={k + 1 === etape ? 'step' : undefined}>
            <span className="int-etape-num" aria-hidden>{k + 1}</span><span>{e}</span>
          </li>
        ))}
      </ol>
      <p className="visuellement-cache" aria-live="polite">Étape {etape} sur 3 : {ETAPES[etape - 1]}</p>
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}

      {etape === 1 && (
        <EtapeEquipement siteId={siteId} site={site.data} chargementSite={site.isLoading} erreurSite={site.isError ? site.error : null}
          choisirSite={(id) => { setSiteId(id); setEquipement(null); setSansEquipement(false); }}
          choisir={(e, sId) => { if (sId) setSiteId(sId); setEquipement(e); setSansEquipement(!e); aller(2); }}
          peutCreerEquipement={peut('site.ecrire')} />
      )}

      {etape === 2 && (
        <form className="pile" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          // Ce qui a été lu sur la photo sert aux pistes de diagnostic : le technicien doit d'abord le confirmer.
          if (lu.aConfirmer && !lu.confirme) { lu.signalerNonConfirme(); return; }
          aller(3);
        }}>
          {resume}
          <Panneau titre="Que constatez-vous ?">
            <div className="pile">
              <LectureAfficheur siteId={siteId} etat={lu} symptome={symptome} codes={codes}
                appliquer={(s, c) => { if (s !== null) setSymptome(s); if (c !== null) setCodes(c); }} />
              <ChampTexte libelle="Symptôme" rows={4} autoFocus value={symptome} onChange={(e) => setSymptome(e.target.value)}
                placeholder="Ex. : la ventilation du 2e étage ne démarre plus, l’écran de l’automate est figé."
                aide={lu.symptomeLu ? <MarqueIa texte={LU_SUR_PHOTO} /> : 'Décrivez ce qui est observé, avec les mots du client si besoin.'} />
              <Champ libelle="Codes défaut affichés" mono value={codes} onChange={(e) => setCodes(e.target.value)} placeholder="Ex. : E05, F12"
                aide={lu.codesLus.length ? <MarqueIa texte={`${LU_SUR_PHOTO} : ${lu.codesLus.join(', ')}`} /> : 'Séparés par une virgule. Laissez vide si aucun code n’est affiché.'} />
              {decouperCodes(codes).length > 0 && (
                <div className="rangee" style={{ gap: 6 }} aria-label="Codes reconnus">
                  {decouperCodes(codes).map((c) => <Badge key={c} ton="neutre" icone={false}><span className="mono">{c}</span></Badge>)}
                </div>
              )}
              {lu.aConfirmer && (
                <div className="photo-confirmation">
                  <label className="case">
                    <input ref={lu.refCase} type="checkbox" checked={lu.confirme} onChange={(e) => lu.setConfirme(e.target.checked)}
                      aria-invalid={!!lu.erreur || undefined} aria-describedby={lu.erreur ? 'confirmation-afficheur-erreur' : undefined} />
                    <span>J’ai vérifié sur l’appareil le symptôme et les codes lus sur la photo.</span>
                  </label>
                  {lu.erreur && <span id="confirmation-afficheur-erreur" className="champ-erreur">{lu.erreur}</span>}
                </div>
              )}
            </div>
          </Panneau>
          <BarreActions>
            <Bouton onClick={() => aller(1)}>Retour</Bouton>
            <Bouton type="submit" variante="principal" disabled={!symptome.trim() && !decouperCodes(codes).length}>Continuer</Bouton>
          </BarreActions>
        </form>
      )}

      {etape === 3 && (
        <form className="pile" onSubmit={(e: FormEvent) => { e.preventDefault(); creer(); }}>
          {resume}
          <Panneau titre="Dans quel contexte ?">
            <div className="pile">
              <p className="petit discret">Tout est facultatif : ce qui n’est pas connu reste « inconnu ».</p>
              <Puces libelle="Fréquence" valeur={ctx.frequence} changer={(v) => setCtx({ ...ctx, frequence: v })}
                options={[{ v: 'permanente', l: 'Permanent' }, { v: 'intermittente', l: 'Intermittent' }]} />
              <Puces libelle="Depuis quand ?" valeur={ctx.depuis ?? null} changer={(v) => setCtx({ ...ctx, depuis: v })}
                options={DEPUIS.map((d) => ({ v: d, l: d }))} />
              <Puces libelle="Modification récente de l’installation ?" valeur={ctx.modification_recente} changer={(v) => setCtx({ ...ctx, modification_recente: v })}
                options={[{ v: 'oui', l: 'Oui' }, { v: 'non', l: 'Non' }, { v: 'inconnu', l: 'Je ne sais pas' }]} />
              {ctx.modification_recente === 'oui' && (
                <ChampTexte libelle="Quelles modifications ?" rows={2} value={ctx.modifications ?? ''} onChange={(e) => setCtx({ ...ctx, modifications: e.target.value })}
                  placeholder="Travaux, mise à jour, remplacement, coupure…" />
              )}
              <ChampTexte libelle="Conditions d’apparition" rows={2} value={ctx.conditions ?? ''} onChange={(e) => setCtx({ ...ctx, conditions: e.target.value })}
                placeholder="Ex. : le matin au démarrage, par forte chaleur, après une coupure secteur" />
              <ChampTexte libelle="Alarmes et messages affichés" rows={2} value={ctx.alarmes ?? ''} onChange={(e) => setCtx({ ...ctx, alarmes: e.target.value })} />
            </div>
          </Panneau>
          <BarreActions>
            <Bouton onClick={() => aller(2)}>Retour</Bouton>
            <Bouton type="submit" variante="principal" chargement={envoi}>Créer l’intervention</Bouton>
          </BarreActions>
        </form>
      )}
    </div>
  );
}

function BarreActions({ children }: { children: ReactNode }) {
  return <div className="int-barre-actions">{children}</div>;
}

// ─── Photo de l'afficheur : symptôme et codes proposés par l'assistance, marqués et à confirmer ───
function useLuSurPhoto() {
  const [symptomeLu, setSymptomeLu] = useState(false);
  const [codesLus, setCodesLus] = useState<string[]>([]);
  const [confirme, setConfirmeBrut] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const refCase = useRef<HTMLInputElement>(null);
  return {
    symptomeLu, codesLus, confirme, erreur, refCase, aConfirmer: symptomeLu || codesLus.length > 0,
    setConfirme: (v: boolean) => { setConfirmeBrut(v); if (v) setErreur(null); },
    marquer: (symptome: boolean, codes: string[]) => {
      if (symptome) setSymptomeLu(true);
      if (codes.length) setCodesLus((x) => [...new Set([...x, ...codes])]);
      setConfirmeBrut(false);
    },
    signalerNonConfirme: () => {
      setErreur('Vérifiez sur l’appareil le symptôme et les codes lus sur la photo, puis cochez la confirmation.');
      refCase.current?.focus();
    },
  };
}
type EtatLu = ReturnType<typeof useLuSurPhoto>;

function LectureAfficheur({ siteId, etat, symptome, codes, appliquer }: {
  siteId: string | null; etat: EtatLu; symptome: string; codes: string; appliquer: (symptome: string | null, codes: string | null) => void;
}) {
  const lp = useLecturePhoto();
  const [lecture, setLecture] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [res, setRes] = useState<Afficheur | null>(null);
  const [proposition, setProposition] = useState<string | null>(null);

  const lire = async (fichier: File) => {
    if (!siteId) return;
    setLecture(true); setErreur(null);
    try {
      const photo = await reduirePhoto(fichier);
      const { afficheur } = await api.envoyer<{ afficheur: Afficheur }>(`/api/sites/${siteId}/lire-afficheur`, formulairePhoto(photo));
      setRes(afficheur);
      const texte = afficheur.symptome ?? (afficheur.message ? `L’afficheur indique : « ${afficheur.message} ».` : null);
      // Un symptôme déjà saisi n'est jamais remplacé : la proposition est montrée à côté.
      let symptomeLu = false;
      if (texte && !symptome.trim()) { appliquer(texte, null); symptomeLu = true; setProposition(null); }
      else setProposition(texte && texte !== symptome.trim() ? texte : null);
      const existants = decouperCodes(codes);
      const nouveaux = afficheur.codes.filter((c) => !existants.includes(c));
      if (nouveaux.length) appliquer(null, [...existants, ...nouveaux].join(', '));
      etat.marquer(symptomeLu, nouveaux);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : e instanceof Error ? e.message : messageErreur(e));
    } finally { setLecture(false); }
  };

  const rien = res && !res.message && !res.codes.length && !res.voyants.length && !res.symptome;
  return (
    <>
      <div className="photo-lecture">
        <BoutonPhoto icone={<ScanText aria-hidden />} surPhoto={lire} chargement={lecture} desactive={!lp.possible || !siteId} explication={lp.raison}>
          Photographier l’afficheur
        </BoutonPhoto>
        {lp.possible && <span className="tres-petit discret">Code défaut, message et voyants sont lus puis proposés, à vérifier. La photo n’est pas conservée.</span>}
      </div>
      {erreur && <Alerte ton="danger" titre="Lecture de l’afficheur impossible">{erreur}</Alerte>}
      {res && (
        <div className="pile photo-resultat" aria-live="polite">
          {rien
            ? <Alerte ton="ambre" titre="Rien de lisible sur la photo">Reprenez-la plus nette, de face et sans reflet, ou saisissez le symptôme à la main.</Alerte>
            : <Alerte ton="info" titre="Afficheur lu par l’assistance">Symptôme et codes pré-remplis : vérifiez-les sur l’appareil et corrigez si besoin.</Alerte>}
          {res.message && <p className="petit">Message affiché : <span className="mono">{res.message}</span></p>}
          {res.voyants.length > 0 && <p className="petit">Voyants : {res.voyants.map((x) => `${x.libelle} : ${x.etat}`).join(' ; ')}</p>}
          {res.illisible.length > 0 && <p className="petit discret">Illisible sur la photo : {res.illisible.join(' ; ')}.</p>}
          {(res.message || res.voyants.length > 0) && <MarqueIa texte={LU_SUR_PHOTO} />}
          {proposition && (
            <div className="pile" style={{ gap: 6 }}>
              <p className="petit">Symptôme proposé (votre saisie n’a pas été remplacée) : « {proposition} »</p>
              <div><Bouton onClick={() => { appliquer(`${symptome.trim()}\n${proposition}`.trim(), null); etat.marquer(true, []); setProposition(null); }}>Ajouter au symptôme</Bouton></div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function EtapeEquipement({ siteId, site, chargementSite, erreurSite, choisirSite, choisir, peutCreerEquipement }: {
  siteId: string | null; site?: SiteDetail; chargementSite: boolean; erreurSite: unknown;
  choisirSite: (id: string | null) => void; choisir: (e: EquipementChoix | null, siteId?: string) => void; peutCreerEquipement: boolean;
}) {
  const [recherche, setRecherche] = useState('');
  const [q, setQ] = useState('');
  const [creation, setCreation] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(recherche.trim()), 250); return () => clearTimeout(t); }, [recherche]);
  const sites = useQuery({ queryKey: ['sites'], enabled: !siteId, queryFn: () => api.get<SiteResume[]>('/api/sites') });
  const globale = useQuery({
    queryKey: ['int-equipements', q], enabled: !siteId && q.length >= 2,
    queryFn: () => api.get<EquipementChoix[]>(`/api/interventions/equipements?q=${encodeURIComponent(q)}`),
  });
  const filtres = useMemo(() => {
    const f = recherche.trim().toLowerCase();
    return (site?.equipements ?? []).filter((e) => !f || [e.label, e.reference, e.manufacturer, e.range_name].some((v) => v?.toLowerCase().includes(f)));
  }, [site, recherche]);

  if (!siteId) {
    return (
      <div className="pile">
        <Panneau titre="Quel équipement est en défaut ?">
          <div className="pile">
            <label className="int-recherche int-recherche-large">
              <Search size={18} aria-hidden className="discret" />
              <span className="visuellement-cache">Rechercher un équipement par référence ou désignation</span>
              <input className="saisie" type="search" autoFocus placeholder="Référence ou désignation (tous vos sites)" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
            </label>
            {q.length >= 2 && globale.isLoading && <Chargement lignes={2} />}
            {globale.data && (globale.data.length === 0
              ? <p className="petit discret">Aucun équipement ne correspond. Choisissez le site ci-dessous, puis déclarez l’équipement comme non répertorié.</p>
              : <div className="liste int-choix">{globale.data.map((e) => (
                <button key={e.id} type="button" className="liste-element int-choix-element" onClick={() => choisir(e, e.site_id)}>
                  <Cpu aria-hidden className="discret" />
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{e.label}</span>
                    <span className="petit discret">{e.site_name} — {[e.manufacturer, e.reference].filter(Boolean).join(' ') || 'Fabricant et référence inconnus'}</span>
                  </span>
                  {e.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                </button>
              ))}</div>)}
          </div>
        </Panneau>
        <Panneau serre titre="Ou choisissez le site">
          {sites.isLoading && <Chargement lignes={3} />}
          {sites.isError && <EtatErreur erreur={sites.error} reessayer={() => sites.refetch()} />}
          {sites.data && sites.data.length === 0 && <EtatVide icone={<Building2 />} titre="Aucun site accessible" texte="Demandez l’accès au site à l’administrateur, ou créez le site." />}
          {sites.data && sites.data.length > 0 && (
            <div className="liste int-choix">
              {sites.data.map((s) => (
                <button key={s.id} type="button" className="liste-element int-choix-element" onClick={() => choisirSite(s.id)}>
                  <Building2 aria-hidden className="discret" />
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{s.name}</span>
                    <span className="petit discret">{[s.client_name, s.city].filter(Boolean).join(' — ') || 'Client et ville non renseignés'}</span>
                  </span>
                  {s.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                </button>
              ))}
            </div>
          )}
        </Panneau>
      </div>
    );
  }

  if (chargementSite) return <Chargement lignes={4} />;
  if (erreurSite || !site) return <EtatErreur erreur={erreurSite} />;

  return (
    <div className="pile">
      <div className="rangee entre">
        <p className="int-resume"><Building2 size={18} aria-hidden /> <strong>{site.name}</strong>
          {site.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}</p>
        <Bouton compact variante="fantome" onClick={() => choisirSite(null)}>Changer de site</Bouton>
      </div>
      {creation
        ? <CreationRapide siteId={site.id} annuler={() => setCreation(false)} cree={(e) => choisir(e)} />
        : (
          <Panneau serre titre="Quel équipement ?">
            <div className="panneau-corps-serre int-filtre-site">
              <label className="int-recherche int-recherche-large">
                <Search size={18} aria-hidden className="discret" />
                <span className="visuellement-cache">Filtrer les équipements du site</span>
                <input className="saisie" type="search" placeholder="Référence ou désignation" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
              </label>
            </div>
            {site.equipements.length === 0 && <EtatVide icone={<Cpu />} titre="Aucun équipement enregistré sur ce site" />}
            {site.equipements.length > 0 && filtres.length === 0 && <p className="petit discret" style={{ padding: '0 20px 12px' }}>Aucun équipement ne correspond.</p>}
            <div className="liste int-choix">
              {filtres.map((e) => (
                <button key={e.id} type="button" className="liste-element int-choix-element" onClick={() => choisir(e)}>
                  <Cpu aria-hidden className="discret" />
                  <span className="liste-element-corps">
                    <span className="liste-element-titre">{e.label}</span>
                    <span className="petit discret">
                      <Valeur v={[e.manufacturer, e.range_name].filter(Boolean).join(' — ')} inconnu="Fabricant inconnu" />
                      {' — '}<Valeur v={e.reference} mono inconnu="Référence inconnue" />
                      {e.location_id && <span className="expert-seulement"> — {cheminLieu(site.lieux, e.location_id)}</span>}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <div className="panneau-corps-serre groupe-btn int-choix-pied">
              {peutCreerEquipement && <Bouton icone={<PackagePlus aria-hidden />} onClick={() => setCreation(true)}>Équipement non répertorié</Bouton>}
              <Bouton variante="fantome" onClick={() => choisir(null)}>Continuer sans désigner d’équipement</Bouton>
            </div>
          </Panneau>
        )}
    </div>
  );
}

function CreationRapide({ siteId, annuler, cree }: { siteId: string; annuler: () => void; cree: (e: EquipementChoix) => void }) {
  const [f, setF] = useState({ label: '', manufacturer: '', reference: '', category: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const qc = useQueryClient();
  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.label.trim()) { setErreurs({ label: 'Indiquez une désignation (ex. « Automate CTA 2 »).' }); return; }
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.post<EquipementChoix>(`/api/sites/${siteId}/equipements`, {
        label: f.label, manufacturer: f.manufacturer || null, reference: f.reference || null, category: f.category || null });
      qc.invalidateQueries({ queryKey: ['site', siteId] });
      cree(r);
    } catch (err) {
      if (err instanceof ErreurApi && Array.isArray(err.details)) setErreurs(Object.fromEntries((err.details as any[]).map((d) => [d.champ, d.message])));
      setErreur(messageErreur(err)); setEnvoi(false);
    }
  };
  return (
    <form onSubmit={soumettre} noValidate>
      <Panneau titre="Équipement non répertorié">
        <div className="pile">
          {erreur && <Alerte ton="danger">{erreur}</Alerte>}
          <p className="petit discret">Laissez vide ce que vous ne connaissez pas : l’information restera affichée comme inconnue. Relevez la plaque signalétique dès que possible.</p>
          <Champ libelle="Désignation" required autoFocus value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} erreur={erreurs.label}
            placeholder="Ex. : Automate de la CTA 2" />
          <div className="grille-form">
            <Champ libelle="Fabricant" value={f.manufacturer} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} />
            <Champ libelle="Référence exacte" mono value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} aide="Telle qu’inscrite sur la plaque." />
          </div>
        </div>
      </Panneau>
      <div className="int-barre-actions">
        <Bouton onClick={annuler}>Retour</Bouton>
        <Bouton type="submit" variante="principal" chargement={envoi}>Enregistrer et continuer</Bouton>
      </div>
    </form>
  );
}
